import React, { useEffect, useMemo, useState } from 'react';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import StatusBanner from '../../components/ui/StatusBanner';
import {
  EmployeeAiPreparation,
  EmployeeDashboardGrid,
  EmployeeDetailList,
  EmployeeSummaryCard,
  EmployeeWorkspaceEmptyState,
  EmployeeWorkspaceSection
} from '../../components/workforce/EmployeeWorkspace';
import { apiRequest } from '../../services/apiClient';
import * as coachingApi from '../../services/coachingApi';

const EMPTY_ASYNC = { status: 'idle', items: [], error: '' };

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value).slice(0, 10) : date.toLocaleDateString();
}

function formatGoalPeriod(goal) {
  if (goal.goalPeriodType === 'yearly') return String(goal.goalYear || 'Yearly');
  if (goal.goalPeriodType === 'quarterly') return `Q${goal.goalQuarter} ${goal.goalYear}`;
  if (goal.goalPeriodType === 'weekly') return `Week ${goal.goalWeek}, ${goal.goalYear}`;
  return goal.periodStart ? String(goal.periodStart).slice(0, 10) : 'Monthly allocation';
}

function formatGoalValue(goal) {
  const value = Number(goal.allocatedValue);
  if (!Number.isFinite(value)) return '—';
  if (goal.unit === 'percentage') return `${value}%`;
  return value.toLocaleString();
}

function activeCertifications(documents) {
  return (Array.isArray(documents) ? documents : []).filter(document => (
    !document.archivedAt && document.documentType === 'certification'
  ));
}

function AsyncSection({ state, emptyTitle, emptyBody, children }) {
  if (state.status === 'loading') return <LoadingSkeleton variant="paragraph" lines={4} />;
  if (state.status === 'error') return <StatusBanner kind="error" message={state.error} />;
  if (!state.items.length) {
    return <EmptyState compact icon="·" headline={emptyTitle} body={emptyBody} />;
  }
  return children;
}

export default function EmployeePerformanceWorkspace({
  profile,
  documents = [],
  canReadDocuments = false,
  canViewCoaching = false,
  attendanceEnabled = false
}) {
  const profileId = Number(profile?.id);
  const managerId = Number(profile?.managerId);
  const hasProfile = Number.isFinite(profileId) && profileId > 0;
  const isManagerBacked = Number.isFinite(managerId) && managerId > 0;
  const [attendance, setAttendance] = useState(attendanceEnabled ? { ...EMPTY_ASYNC, status: 'loading' } : EMPTY_ASYNC);
  const [coaching, setCoaching] = useState(canViewCoaching ? { ...EMPTY_ASYNC, status: 'loading' } : EMPTY_ASYNC);
  const [goals, setGoals] = useState(isManagerBacked ? { ...EMPTY_ASYNC, status: 'loading' } : EMPTY_ASYNC);

  useEffect(() => {
    if (!attendanceEnabled || !hasProfile) {
      setAttendance(EMPTY_ASYNC);
      return undefined;
    }
    let cancelled = false;
    const controller = new AbortController();
    setAttendance({ ...EMPTY_ASYNC, status: 'loading' });
    apiRequest(`/api/employees/profiles/${profileId}/attendance`, { signal: controller.signal })
      .then(data => {
        if (cancelled) return;
        const events = Array.isArray(data?.events) ? data.events : [];
        setAttendance({ status: 'loaded', items: data?.summary ? [{ ...data.summary, events }] : events.length ? [{ events }] : [], error: '' });
      })
      .catch(error => {
        if (!cancelled) setAttendance({ status: 'error', items: [], error: error?.message || 'Unable to load attendance information.' });
      });
    return () => { cancelled = true; controller.abort(); };
  }, [attendanceEnabled, hasProfile, profileId]);

  useEffect(() => {
    if (!canViewCoaching || !hasProfile) {
      setCoaching(EMPTY_ASYNC);
      return undefined;
    }
    let cancelled = false;
    setCoaching({ ...EMPTY_ASYNC, status: 'loading' });
    coachingApi.listNotesForSubject({ employeeProfileId: profileId, managerId: isManagerBacked ? managerId : null, limit: 10 })
      .then(items => { if (!cancelled) setCoaching({ status: 'loaded', items: Array.isArray(items) ? items : [], error: '' }); })
      .catch(error => {
        if (!cancelled) setCoaching({ status: 'error', items: [], error: error?.message || 'Unable to load coaching activity.' });
      });
    return () => { cancelled = true; };
  }, [canViewCoaching, hasProfile, isManagerBacked, managerId, profileId]);

  useEffect(() => {
    if (!isManagerBacked) {
      setGoals(EMPTY_ASYNC);
      return undefined;
    }
    let cancelled = false;
    const controller = new AbortController();
    setGoals({ ...EMPTY_ASYNC, status: 'loading' });
    apiRequest(`/api/goals/manager/${managerId}`, { signal: controller.signal })
      .then(items => { if (!cancelled) setGoals({ status: 'loaded', items: Array.isArray(items) ? items : [], error: '' }); })
      .catch(error => {
        if (!cancelled) setGoals({ status: 'error', items: [], error: error?.message || 'Unable to load goal allocations.' });
      });
    return () => { cancelled = true; controller.abort(); };
  }, [isManagerBacked, managerId]);

  const certifications = useMemo(() => activeCertifications(documents), [documents]);
  const attendanceRow = attendance.items[0] || null;
  const attendanceEvents = useMemo(() => attendanceRow?.events || [], [attendanceRow]);
  const latestCoaching = coaching.items[0] || null;
  const history = useMemo(() => {
    const entries = [];
    coaching.items.forEach(note => entries.push({
      id: `coaching-${note.id}`,
      date: note.createdAt,
      type: 'Coaching',
      label: 'Leadership coaching note recorded'
    }));
    attendanceEvents.forEach(event => entries.push({
      id: `attendance-${event.id}`,
      date: event.createdAt || event.startDate,
      type: 'Attendance',
      label: `${String(event.eventType || 'Attendance event').replaceAll('_', ' ')} · ${event.status || 'Recorded'}`
    }));
    certifications.forEach(document => entries.push({
      id: `certification-${document.id}`,
      date: document.uploadedAt || document.createdAt,
      type: 'Certification',
      label: document.title || document.fileName || 'Certification recorded'
    }));
    return entries.filter(entry => entry.date).sort((a, b) => String(b.date).localeCompare(String(a.date))).slice(0, 8);
  }, [attendanceEvents, certifications, coaching.items]);

  return (
    <div className="employee-performance" data-testid="employee-performance-workspace">
      <EmployeeWorkspaceSection title="Performance summary" description="Current employee-scoped records; no combined score is calculated">
        <EmployeeDashboardGrid columns="wide">
          <EmployeeSummaryCard label="Reviews" value="Not available" detail="Employee reviews are not supported yet" />
          <EmployeeSummaryCard label="Goal allocations" value={isManagerBacked ? (goals.status === 'loaded' ? goals.items.length : '—') : 'Not available'} detail={isManagerBacked ? 'Manager-backed operational allocations' : 'No employee-level goal model'} />
          <EmployeeSummaryCard label="Recent coaching" value={canViewCoaching ? (coaching.status === 'loaded' ? coaching.items.length : '—') : 'Restricted'} detail={canViewCoaching ? (latestCoaching ? `Last recorded ${formatDate(latestCoaching.createdAt)}` : 'No coaching recorded') : 'Leadership access required'} />
          <EmployeeSummaryCard label="Attendance" value={attendanceEnabled ? (attendanceRow?.alertLevel || 'No signal') : 'Not enabled'} detail={attendanceRow ? `${attendanceRow.totalIncidents30 || 0} incidents in 30 days` : 'No attendance summary available'} tone={attendanceRow?.alertLevel === 'Critical' ? 'warning' : 'neutral'} />
          <EmployeeSummaryCard label="Certifications" value={canReadDocuments ? certifications.length : 'Restricted'} detail={canReadDocuments ? 'Active certification records' : 'Document access required'} />
        </EmployeeDashboardGrid>
      </EmployeeWorkspaceSection>

      <EmployeeWorkspaceSection title="Goals" description="Auditable goal allocations for the employee's manager-backed roster identity">
        {isManagerBacked ? (
          <AsyncSection state={goals} emptyTitle="No goal allocations available" emptyBody="No goal allocations are recorded for this manager-backed employee.">
            <ul className="employee-performance__records" data-testid="employee-performance-goals">
              {goals.items.map(goal => (
                <li key={goal.id}>
                  <strong>{goal.kpiLabel || goal.kpiKey || 'Goal allocation'}</strong>
                  <span>{formatGoalValue(goal)} · {formatGoalPeriod(goal)} · {goal.locationCode || 'Location unavailable'}</span>
                  <span>Owner: {goal.managerName || `Manager #${managerId}`} · Allocated {formatDate(goal.allocatedAt) || 'date unavailable'}</span>
                </li>
              ))}
            </ul>
          </AsyncSection>
        ) : (
          <EmployeeWorkspaceEmptyState title="Employee goals are planned" description="The current goal model supports location goals and manager allocations, not standalone employee goals." />
        )}
      </EmployeeWorkspaceSection>

      <EmployeeDashboardGrid>
        <EmployeeWorkspaceSection title="Reviews and assessments" description="Planned capability">
          <EmployeeWorkspaceEmptyState title="No employee reviews available" description="The current data model does not contain employee reviews, ratings, or assessments." />
        </EmployeeWorkspaceSection>

        <EmployeeWorkspaceSection title="Coaching relationship" description="Leadership-authorized coaching activity only">
          {canViewCoaching ? (
            <AsyncSection state={coaching} emptyTitle="No coaching recorded" emptyBody="Authorized coaching activity will appear here after it is recorded.">
              <EmployeeDetailList items={[
                { label: 'Recent records', value: coaching.items.length },
                { label: 'Last coaching date', value: formatDate(latestCoaching?.createdAt) },
                { label: 'Visibility', value: 'Leadership only' }
              ]} />
            </AsyncSection>
          ) : (
            <EmptyState compact icon="·" headline="Coaching access restricted" body="Leadership coaching information is not available with your current permissions." />
          )}
        </EmployeeWorkspaceSection>
      </EmployeeDashboardGrid>

      <EmployeeDashboardGrid>
        <EmployeeWorkspaceSection title="Operational performance" description="Employee-level data boundary">
          <EmployeeWorkspaceEmptyState title="Operational performance unavailable" description="Employee-level operational performance is not available in the current data model." release="Current model" />
        </EmployeeWorkspaceSection>

        <EmployeeWorkspaceSection title="Attendance and reliability" description="Existing employee-scoped attendance signals">
          {!attendanceEnabled ? (
            <EmptyState compact icon="·" headline="Attendance is not enabled" body="No attendance information is available for this tenant configuration." />
          ) : (
            <AsyncSection state={attendance} emptyTitle="No attendance data yet" emptyBody="Employee-scoped attendance signals will appear after attendance activity is recorded.">
              <EmployeeDetailList items={[
                { label: 'Alert level', value: attendanceRow?.alertLevel },
                { label: '30-day incidents', value: attendanceRow?.totalIncidents30 ?? 0 },
                { label: 'Recent events', value: attendanceEvents.length }
              ]} />
            </AsyncSection>
          )}
        </EmployeeWorkspaceSection>
      </EmployeeDashboardGrid>

      <EmployeeWorkspaceSection title="Performance history" description="Chronological employee-scoped records from supported domains">
        {history.length ? (
          <ol className="employee-performance__history" data-testid="employee-performance-history">
            {history.map(entry => (
              <li key={entry.id}>
                <time dateTime={String(entry.date)}>{formatDate(entry.date)}</time>
                <div><strong>{entry.type}</strong><span>{entry.label}</span></div>
              </li>
            ))}
          </ol>
        ) : (
          <EmptyState compact icon="·" headline="No performance history available" body="Supported coaching, attendance, and certification events will appear here when recorded and authorized." />
        )}
      </EmployeeWorkspaceSection>

      <EmployeeAiPreparation />
    </div>
  );
}
